import React, { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";

export default function PressReleasePage() {
  const [accessCode, setAccessCode] = useState("");
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [error, setError] = useState("");

  const handleUnlock = () => {
    if (accessCode === "713love" || accessCode === "KazKayleyAnne") {
      setIsUnlocked(true);
      setError("");
    } else {
      setError("Invalid access code");
    }
  };

  if (!isUnlocked) {
    return (
      <Layout>
        <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center px-4" data-testid="press-gate">
          <div className="card-glass p-8 max-w-md w-full text-center rounded-2xl">
            <svg className="w-12 h-12 text-[#A3E635] mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
            <h2 className="font-heading text-2xl font-bold text-white mb-2">Press Access</h2>
            <p className="text-[#E8E4DC] text-sm mb-6">Enter the access code to view press materials.</p>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={accessCode}
                onChange={(e) => { setAccessCode(e.target.value); setError(""); }}
                onKeyDown={(e) => e.key === "Enter" && handleUnlock()}
                className="input-dark flex-1 text-sm text-center"
                placeholder="Access code"
                data-testid="press-code-input"
              />
              <button onClick={handleUnlock} className="btn-primary px-6 text-sm" data-testid="press-code-submit">Enter</button>
            </div>
            {error && <p className="text-red-400 text-xs" data-testid="press-code-error">{error}</p>}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-[calc(100vh-8rem)] py-16 px-4" data-testid="press-release-page">
        <article className="max-w-4xl mx-auto">

          {/* Header */}
          <div className="text-center mb-12">
            <p className="text-[#A3E635] text-sm font-semibold tracking-widest uppercase mb-4">For Immediate Release</p>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4" data-testid="press-title">
              Houston-Based Startup Launches Top Screen&#8482; &mdash; A Revolutionary Always-On-Top Picture-in-Picture Platform with Lifetime Licensing for Just $18.99
            </h1>
            <p className="text-[#E8E4DC] text-base sm:text-lg max-w-3xl mx-auto leading-relaxed">
              Cross-platform application redefines multitasking with a white-label-ready, full-stack architecture built for consumers, enterprises, and investors alike.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 card-glass px-5 py-2 rounded-full">
              <span className="text-[#E8E4DC] text-sm">Houston, TX &mdash; March 21, 2026</span>
            </div>
          </div>

          {/* Body */}
          <div className="card-glass rounded-2xl p-6 sm:p-10 space-y-8 text-[#E8E4DC] leading-relaxed" style={{ background: "rgba(0,0,0,0.5)" }}>

            {/* Lead */}
            <p className="text-base sm:text-lg">
              <strong className="text-white">713 Consulting &amp; Development</strong> today announced the official launch of <strong className="text-[#A3E635]">Top Screen&#8482;</strong>, a first-of-its-kind, fully customizable Always-On-Top picture-in-picture (PiP) platform that lets users pin any content &mdash; live tabs, websites, videos, dashboards, and more &mdash; above every other window on their screen. Available now across Chrome, Edge, Windows 11, iOS, and Android, Top Screen&#8482; is offered under a <strong className="text-white">one-time $18.99 lifetime license</strong> with absolutely no monthly or annual fees &mdash; ever.
            </p>

            <p className="text-base">
              In a software landscape dominated by recurring subscriptions and price increases, Top Screen&#8482; bucks the trend entirely. For less than the cost of a single month of most SaaS tools, early adopters receive permanent access to every feature, every future update, and every platform &mdash; for life.
            </p>

            {/* Promo Banner */}
            <div className="rounded-xl p-6 text-center" style={{ background: "linear-gradient(135deg, rgba(163,247,58,0.12), rgba(163,247,58,0.04))", border: "1px solid rgba(163,247,58,0.25)" }}>
              <p className="text-[#A3E635] font-bold text-xl mb-1">EARLY ADOPTER OFFER &mdash; 50% OFF</p>
              <p className="text-white text-lg mb-3">
                Use promo code <span className="font-mono bg-white/10 px-3 py-1 rounded text-[#A3E635] font-bold">FAMILY2026</span> at checkout for 50% off the lifetime license.
              </p>
              <p className="text-sm text-[#E8E4DC] mb-4">Limited to the first <strong className="text-white">10,000 early adopters</strong>. Once they&rsquo;re claimed, this deal is gone for good.</p>
              <Link to="/pricing" className="inline-block bg-[#A3E635] text-black font-bold px-8 py-3 rounded-full hover:bg-[#8CD025] transition-colors" data-testid="press-cta">
                Download Top Screen&#8482; &amp; Claim Your Discount
              </Link>
            </div>

            {/* The Problem */}
            <div>
              <h2 className="font-heading text-xl sm:text-2xl font-bold text-white mb-3">The Problem: Modern Multitasking Is Broken</h2>
              <p className="text-base">
                Professionals, students, content creators, and everyday users constantly juggle windows &mdash; switching between a video call and notes, a trading dashboard and a news feed, a tutorial and the project they&rsquo;re building. Current operating systems force users to choose: tile windows awkwardly, or lose sight of critical content entirely. Top Screen&#8482; eliminates this friction by giving any piece of content a persistent, always-visible, fully interactive floating window.
              </p>
            </div>

            {/* Extension Features */}
            <div>
              <h2 className="font-heading text-xl sm:text-2xl font-bold text-white mb-3">Chrome &amp; Edge Extension &mdash; Power at a Click</h2>
              <p className="text-base mb-4">
                The Top Screen&#8482; browser extension (Manifest V3) transforms how users interact with their browser. Key capabilities include:
              </p>
              <ul className="space-y-2 ml-1">
                {[
                  ["PiP Current Tab (Beta)", "Capture your entire active tab and float it as an always-on-top window. Ideal for monitoring dashboards, live streams, or video calls while working in another tab."],
                  ["PiP Any Website", "Enter any URL and instantly open it in a fully interactive floating window with complete mouse, keyboard, and touch control. Click links, fill forms, scroll \u2014 just like a regular browser window, but always on top."],
                  ["PiP Page Videos", "Detect and pop out any video playing on the current page into its own dedicated PiP window with independent audio controls."],
                  ["Multiple Simultaneous PiP Windows", "Run several floating windows at once. Monitor a stock ticker, a chat window, and a tutorial video simultaneously \u2014 each in its own adjustable PiP instance."],
                  ["Custom Frame Styles", "Choose from Classic, RGB Backlight (animated rainbow border), Neon Glow (pulsing border), or Shadow frames. Every frame\u2019s color and width are fully adjustable."],
                  ["Theatre Mode", "Dim the entire background page to 75% opacity for an immersive, cinema-like focus on your PiP content."],
                  ["Per-Instance Audio Override", "Mute or unmute each PiP window individually, or mute all at once with a single button. No more fighting with browser tab audio."],
                  ["Real-Time Opacity & Size Controls", "Adjust transparency from 20% to 100% and choose from Small, Medium, Large, or XL window sizes. Position windows in any corner with one click."],
                  ["Account Sync", "Sign in with Google or email to sync your settings across devices and unlock your lifetime license on every platform."],
                ].map(([title, desc], i) => (
                  <li key={title} className="flex items-start gap-3">
                    <svg className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#A3E635]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                    <span><strong className="text-white">{title}:</strong> {desc}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Desktop & Mobile */}
            <div>
              <h2 className="font-heading text-xl sm:text-2xl font-bold text-white mb-3">Windows 11 Desktop &amp; Mobile Apps</h2>
              <p className="text-base mb-4">
                Beyond the browser, Top Screen&#8482; delivers native experiences on Windows 11 (Electron) and iOS/Android (React Native):
              </p>
              <ul className="space-y-2 ml-1">
                {[
                  ["True Always-On-Top Windows", "Desktop PiP windows that stay above every other application \u2014 including full-screen apps \u2014 with system tray integration and a Ctrl+Shift+P global keyboard shortcut."],
                  ["Timer & Alarm System", "Schedule content to pop up at specific times with labeled alarms, datetime pickers, and optional URL auto-launch. Perfect for reminders, scheduled monitoring, and time-sensitive workflows."],
                  ["Native Mobile PiP", "Android\u2019s native PiP mode and iOS WebView integration allow floating content over any app on your phone or tablet."],
                  ["Secure Credential Storage", "Platform-native secure storage protects login tokens and user data on every device."],
                ].map(([title, desc], i) => (
                  <li key={title} className="flex items-start gap-3">
                    <svg className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#60a5fa]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                    <span><strong className="text-white">{title}:</strong> {desc}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Web Platform & Backend */}
            <div>
              <h2 className="font-heading text-xl sm:text-2xl font-bold text-white mb-3">A Full-Stack Platform Built for Scale &mdash; and White Labeling</h2>
              <p className="text-base mb-4">
                Top Screen&#8482; isn&rsquo;t just a consumer app &mdash; it&rsquo;s a production-grade, white-label-ready full-stack platform that technology companies, agencies, and entrepreneurs can license and rebrand for virtually any vertical. The technology stack includes:
              </p>
              <ul className="space-y-2 ml-1">
                {[
                  ["React + Tailwind CSS Frontend", "17+ modular page files with animated gradient backgrounds, frosted-glass UI, parallax scrolling, staggered entrance animations, and a real-time scrolling news ticker aggregating RSS feeds from major outlets."],
                  ["FastAPI + MongoDB Backend", "Modular route architecture (auth, CRM, admin, payments, teams, PiP sessions, calendar integration, news) with JWT + Google OAuth authentication, 72-hour free trial management, and role-based access control."],
                  ["Built-In CRM with Phonebook & Calendar", "Full CRUD contact management with custom groups, CSV/vCard import, cloud-hosted photos with server-side compression and circular crop tools, Google Maps location links, event sharing, and a calendar with Google Calendar, Microsoft Outlook, and Apple CalDAV integration."],
                  ["PiP Session Dashboard", "Real-time session management across all connected devices with bulk mute-all, unmute-all, and close-all controls."],
                  ["Team Collaboration", "Built-in team CRUD enabling shared PiP sessions and collaborative workflows."],
                  ["Admin Update Log", "Platform-specific auto-incrementing version tracking, admin name logging, timestamped notes, and a public-facing changelog."],
                  ["Cloud Object Storage", "Emergent Object Storage integration with automatic JPEG compression (max 1200px @ 82% quality), thumbnail generation (150px @ 70%), and lazy-loaded image delivery for fast CRM performance."],
                  ["Payment Processing", "PayPal IPN integration with server-validated promotional discount codes, transaction recording, and discount usage tracking. Stripe-ready architecture."],
                  ["Password-Protected Downloads", "Administrator-gated download portal serving versioned ZIP packages for all platforms with real file sizes and working download links."],
                  ["SendGrid Email Integration", "Automated welcome emails and transactional notifications with branded HTML templates."],
                ].map(([title, desc], i) => (
                  <li key={title} className="flex items-start gap-3">
                    <svg className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#f472b6]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                    <span><strong className="text-white">{title}:</strong> {desc}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* White Label */}
            <div>
              <h2 className="font-heading text-xl sm:text-2xl font-bold text-white mb-3">White-Label Opportunity for Investors &amp; Businesses</h2>
              <p className="text-base">
                Every layer of Top Screen&#8482; &mdash; from the animated frontend to the modular API backend &mdash; is architected for white-label deployment. Swap the branding, connect your own database, configure your own payment processor, and launch a fully functional SaaS product in a fraction of the time and cost of building from scratch. Potential verticals include:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
                {[
                  "Telehealth & Remote Patient Monitoring",
                  "Financial Trading Dashboards",
                  "Online Education & Tutoring",
                  "Remote Team Collaboration",
                  "Live Streaming & Content Creation",
                  "Customer Support & CRM",
                  "Real Estate Virtual Tours",
                  "E-Commerce & Live Shopping",
                  "Security & Surveillance Monitoring",
                ].map((v) => (
                  <div key={v} className="card-glass rounded-lg px-3 py-2 text-sm text-center" style={{ borderColor: "rgba(163,247,58,0.15)" }}>
                    {v}
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing & Value */}
            <div>
              <h2 className="font-heading text-xl sm:text-2xl font-bold text-white mb-3">Lifetime License &mdash; No Subscriptions, No Catches</h2>
              <p className="text-base">
                While competitors charge $5&ndash;$15/month for basic PiP functionality, Top Screen&#8482; offers a <strong className="text-white">one-time payment of $18.99</strong> for lifetime access across every platform, every feature, and every future update. There is no monthly billing. No annual renewal. No feature gating. Every user gets 100% of all features during a 72-hour free trial &mdash; enough time to experience firsthand why this is the most versatile multitasking tool on the market.
              </p>
              <p className="text-base mt-3">
                For a limited time, the first <strong className="text-white">10,000 early adopters</strong> can use promo code <span className="font-mono bg-white/10 px-2 py-0.5 rounded text-[#A3E635] font-bold">FAMILY2026</span> to receive <strong className="text-white">50% off</strong>, bringing the lifetime price down to just <strong className="text-[#A3E635]">$9.50</strong>. Once these spots are claimed, the promotion ends permanently.
              </p>
            </div>

            {/* Download CTA */}
            <div className="rounded-xl p-6 text-center" style={{ background: "linear-gradient(135deg, rgba(163,247,58,0.12), rgba(163,247,58,0.04))", border: "1px solid rgba(163,247,58,0.25)" }}>
              <p className="text-white font-bold text-lg mb-2">Download Top Screen&#8482; Now</p>
              <p className="text-[#E8E4DC] text-sm mb-4">
                Visit the link below to download the app, start your free 72-hour trial, and apply promo code <span className="font-mono text-[#A3E635] font-bold">FAMILY2026</span> for 50% off your lifetime license.
              </p>
              <Link to="/install" className="inline-block bg-[#A3E635] text-black font-bold px-8 py-3 rounded-full hover:bg-[#8CD025] transition-colors" data-testid="press-download-cta">
                topscreen.org/install
              </Link>
            </div>

            {/* About */}
            <div>
              <h2 className="font-heading text-xl sm:text-2xl font-bold text-white mb-3">About 713 Consulting &amp; Development</h2>
              <p className="text-base">
                Based in Houston, Texas, 713 Consulting &amp; Development (713 C &amp; D) is a technology startup specializing in innovative software solutions that simplify how people interact with digital content. Top Screen&#8482; is the company&rsquo;s flagship product, representing years of development focused on creating the most comprehensive, cross-platform multitasking tool available today.
              </p>
            </div>

            {/* Contact */}
            <div className="rounded-xl p-6" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
              <h3 className="font-heading text-lg font-bold text-white mb-4">Media &amp; Investor Contact</h3>
              <div className="space-y-2 text-sm">
                <p><strong className="text-white">Keith Bolden</strong> &mdash; Founder / CEO</p>
                <p>713 C &amp; D and Top Screen&#8482;</p>
                <p>
                  Phone: <a href="tel:+17133289736" className="text-[#A3E635] hover:underline">713.328.9736</a> (call or text)
                </p>
                <p>
                  Email: <a href="mailto:713Consulting@gmail.com" className="text-[#A3E635] hover:underline">713Consulting@gmail.com</a>
                </p>
                <p className="pt-1 text-[#E8E4DC]">
                  713 Consulting &amp; Development<br />
                  1942 W. Gray St., Box #1259<br />
                  Houston, TX 77019
                </p>
              </div>
            </div>

            {/* Closing */}
            <p className="text-center text-sm text-[#8888a0]">
              ###
            </p>
            <p className="text-center text-xs text-[#55556a]">
              Top Screen&#8482; is a trademark of 713 Consulting &amp; Development. All other trademarks are the property of their respective owners.
            </p>

          </div>
        </article>
      </div>
    </Layout>
  );
}
